import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  TargetStatsType,
  GetTargetStatsGQL,
  GetFreebieTransactionByByPartnershipNetworkPast45MinutesGQL,
  FreebieTransactionType,
  RedeemFrebiesGQL,
  GetFreebieTransactionByTargetPast45MinutesGQL,
  IsFreebiesRedeemableGQL,
  GetCurrentUserLastUnredeemedCouponGQL,
  PurchaseCouponGQL,
  FindCouponSettingsByTargetGQL,
  DiscountDtoInput,
  CouponSettingsType,
  CouponType,
} from '@sifca-monorepo/widget-generator';

@Injectable({
  providedIn: 'root',
})
export class MobilePartnerService {
  private isLast: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private coupon: BehaviorSubject<CouponType> = new BehaviorSubject<CouponType>(null);
  private loadingFreebies: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private stats: BehaviorSubject<TargetStatsType> = new BehaviorSubject<TargetStatsType>(null);
  private freebies: BehaviorSubject<FreebieTransactionType[]> = new BehaviorSubject<FreebieTransactionType[]>(null);

  get coupon$(): Observable<CouponType> {
    return this.coupon.asObservable();
  }
  set coupon$(value: CouponType) {
    this.coupon.next(value);
  }

  get loadingFreebies$(): Observable<boolean> {
    return this.loadingFreebies.asObservable();
  }

  get isLast$(): Observable<boolean> {
    return this.isLast.asObservable();
  }

  get stats$(): Observable<TargetStatsType> {
    return this.stats.asObservable();
  }

  get freebies$(): Observable<FreebieTransactionType[]> {
    return this.freebies.asObservable();
  }
  set freebies$(value: any) {
    this.freebies.next(value);
  }

  constructor(
    private redeemFrebiesGQL: RedeemFrebiesGQL,
    private getTargetStatsGQL: GetTargetStatsGQL,
    private purchaseCouponGQL: PurchaseCouponGQL,
    private isFreebiesRedeemableGQL: IsFreebiesRedeemableGQL,
    private findCouponSettingsByTargetGQL: FindCouponSettingsByTargetGQL,
    private getCurrentUserLastUnredeemedCouponGQL: GetCurrentUserLastUnredeemedCouponGQL,
    private getFreebieTransactionByTargetPast45MinutesGQL: GetFreebieTransactionByTargetPast45MinutesGQL,
    private getFreebieTransactionByByPartnershipNetworkPast45MinutesGQL: GetFreebieTransactionByByPartnershipNetworkPast45MinutesGQL,
  ) {}

  purchaseCoupon(amount: string, discount: DiscountDtoInput, pos: string): Observable<CouponType> {
    return this.purchaseCouponGQL.mutate({ amount, discount, target: { pos } }).pipe(
      map(({ data }: any) => {
        if (data) {
          this.coupon.next(data.purchaseCoupon);
          return data.purchaseCoupon;
        }
      }),
    );
  }

  getCurrentUserLastUnredeemedCoupon(pos: string): Observable<CouponType> {
    return this.getCurrentUserLastUnredeemedCouponGQL.fetch({ target: { pos } }).pipe(
      map(({ data }: any) => {
        if (data) {
          this.coupon.next(data.getCurrentUserLastUnredeemedCoupon);
          return data.getCurrentUserLastUnredeemedCoupon;
        }
      }),
    );
  }

  findCouponSettingsByTarget(pos: string): Observable<CouponSettingsType[]> {
    return this.findCouponSettingsByTargetGQL.fetch({ target: { pos } }).pipe(
      map(({ data }: any) => {
        if (data) {
          return data.findCouponSettingsByTarget;
        }
      }),
    );
  }

  isFreebiesRedeemable(id: string): Observable<any> {
    return this.isFreebiesRedeemableGQL.fetch({ id }).pipe(
      map(({ data }: any) => {
        if (data) {
          return data.isFreebiesRedeemable.success;
        }
      }),
    );
  }

  getFreebieTransactionByTargetPast45Minutes(pos: string): Observable<FreebieTransactionType[]> {
    this.loadingFreebies.next(true);
    return this.getFreebieTransactionByTargetPast45MinutesGQL
      .fetch({
        pagination: { limit: 50 },
        target: { pos },
      })
      .pipe(
        catchError((error) => {
          this.loadingFreebies.next(false);
          throw error;
        }),
        map(({ data }: any) => {
          if (data) {
            this.loadingFreebies.next(false);
            this.freebies.next(data.getFreebieTransactionByTargetPast45Minutes.objects);
            return data.getFreebieTransactionByTargetPast45Minutes.objects;
          }
        }),
      );
  }

  getFreebieTransactionByByPartnershipNetworkPast45Minutes(): Observable<FreebieTransactionType[]> {
    return this.getFreebieTransactionByByPartnershipNetworkPast45MinutesGQL
      .fetch({
        pagination: { limit: 50 },
        target: { pos: localStorage.getItem('posId') },
      })
      .pipe(
        catchError((error) => {
          throw error;
        }),
        map(({ data }: any) => {
          if (data) {
            this.freebies.next(data.getFreebieTransactionByByPartnershipNetworkPast45Minutes.objects);
            return data.getFreebieTransactionByByPartnershipNetworkPast45Minutes.objects;
          }
        }),
      );
  }

  redeemFrebies(id: string): Observable<any> {
    return this.redeemFrebiesGQL
      .mutate({
        id,
      })
      .pipe(
        map(({ data }: any) => {
          if (data) {
            return data.redeemFrebies;
          }
        }),
      );
  }

  getTargetStats(pos): Observable<TargetStatsType> {
    return this.getTargetStatsGQL
      .fetch({
        target: { pos },
      })
      .pipe(
        catchError((error) => {
          throw error;
        }),
        map(({ data }: any) => {
          if (data) {
            this.stats.next(data.getTargetStats);
            return data.getTargetStats;
          }
        }),
      );
  }
}
